/**
 * "New dispatch" and "Add a missed item" forms (F7.8), validated like uc-core's CreateDispatchDto /
 * DispatchCorrectionDto and the service rules: clean vehicle, buyer org, released lots not on hold,
 * never more pouches than are left at the hall (the same lot on two rows counts together).
 */
import { humanize } from '../../labels'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const LINK_LOT = /\/10\/(PL-\d{4}-\d{4}-\d{4}-[A-Z0-9])\s*$/i

/** A typed code or a scanned carton / pouch link → the pack lot code (upper case). */
export function normalizePackLotCode(raw: string): string {
  const t = raw.trim()
  const m = LINK_LOT.exec(t)
  return (m ? m[1]! : t).toUpperCase()
}

export interface DispatchItemInput {
  key: string
  code: string
  pouches: number | null
}

export interface DispatchFormValues {
  fromLocationId: string
  toOrgId: string
  challanNo: string
  vehicleNo: string
  driverName: string
  driverPhone: string
  vehicleClean: boolean
  items: DispatchItemInput[]
}

export interface DispatchFormErrors {
  fromLocationId?: string
  toOrgId?: string
  challanNo?: string
  vehicleNo?: string
  driverName?: string
  driverPhone?: string
  vehicleClean?: string
  /** Form-level item problem (no rows). */
  itemsForm?: string
  items: Record<string, { code?: string; pouches?: string }>
}

/** What the form knows about a releasable lot: pouches still at the hall. */
export interface LotLeft {
  code: string
  pouchesLeft: number
}

export const emptyDispatchForm = (): DispatchFormValues => ({
  fromLocationId: '',
  toOrgId: '',
  challanNo: '',
  vehicleNo: '',
  driverName: '',
  driverPhone: '',
  vehicleClean: false,
  items: [{ key: 'i1', code: '', pouches: null }],
})

export const hasDispatchErrors = (e: DispatchFormErrors) =>
  Object.entries(e).some(([k, v]) => (k === 'items' ? Object.keys(v as object).length > 0 : Boolean(v)))

/**
 * `released` maps code → pouches left for the lots that may go. When `complete` is true the map
 * holds every such lot, so a code missing from it is refused here; otherwise the server decides.
 */
export function validateDispatchForm(
  f: DispatchFormValues,
  released: ReadonlyMap<string, LotLeft>,
  complete: boolean,
): DispatchFormErrors {
  const e: DispatchFormErrors = { items: {} }
  if (!UUID.test(f.fromLocationId)) e.fromLocationId = 'Pick the packing hall'
  if (!UUID.test(f.toOrgId)) e.toOrgId = 'Pick the customer'
  const challan = f.challanNo.trim()
  if (!challan) e.challanNo = 'Type the invoice or challan number'
  else if (challan.length > 30) e.challanNo = 'At most 30 characters'
  const vehicle = f.vehicleNo.trim()
  if (!vehicle) e.vehicleNo = 'Type the vehicle number'
  else if (vehicle.length > 20) e.vehicleNo = 'At most 20 characters'
  if (f.driverName.trim().length > 80) e.driverName = 'At most 80 characters'
  if (f.driverPhone.trim() && !/^\d{10}$/.test(f.driverPhone.trim())) e.driverPhone = 'Ten digits'
  if (!f.vehicleClean) e.vehicleClean = 'The vehicle bed must be empty and clean before loading'

  const rows = f.items.filter((i) => i.code.trim() || i.pouches !== null)
  if (rows.length === 0) e.itemsForm = 'Add at least one pack lot'
  if (rows.length > 100) e.itemsForm = 'At most 100 rows on one trip'

  const totals = new Map<string, number>()
  for (const i of rows) {
    const code = normalizePackLotCode(i.code)
    if (code && i.pouches) totals.set(code, (totals.get(code) ?? 0) + i.pouches)
  }
  for (const i of rows) {
    const errs: { code?: string; pouches?: string } = {}
    const code = normalizePackLotCode(i.code)
    const lot = code ? released.get(code) : undefined
    if (!code) errs.code = 'Scan or type the pack lot'
    else if (code.length > 200) errs.code = 'Too long'
    else if (!lot && complete) errs.code = 'Not a released pack lot that is free to go'
    if (i.pouches === null || !Number.isInteger(i.pouches) || i.pouches < 1) errs.pouches = 'At least 1 pouch'
    else if (lot && (totals.get(code) ?? 0) > lot.pouchesLeft)
      errs.pouches =
        lot.pouchesLeft === 0 ? 'No pouches of this lot are left at the hall' : `Only ${lot.pouchesLeft} left at the hall`
    if (errs.code || errs.pouches) e.items[i.key] = errs
  }
  return e
}

export interface DispatchBody {
  fromLocationId: string
  toOrgId: string
  challanNo: string
  vehicleNo: string
  driverName?: string
  driverPhone?: string
  vehicleClean: boolean
  items: Array<{ packLotCode: string; pouches: number }>
}

export function toDispatchBody(f: DispatchFormValues): DispatchBody {
  const name = f.driverName.trim()
  const phone = f.driverPhone.trim()
  return {
    fromLocationId: f.fromLocationId,
    toOrgId: f.toOrgId,
    challanNo: f.challanNo.trim(),
    vehicleNo: f.vehicleNo.trim().toUpperCase(),
    ...(name ? { driverName: name } : {}),
    ...(phone ? { driverPhone: phone } : {}),
    vehicleClean: f.vehicleClean,
    items: f.items
      .filter((i) => i.code.trim() && i.pouches)
      .map((i) => ({ packLotCode: normalizePackLotCode(i.code), pouches: i.pouches! })),
  }
}

/** The 422 codes of POST /dispatches in plain sentences. */
export const DISPATCH_ERROR_SENTENCE: Record<string, string> = {
  VEHICLE_NOT_CLEAN: 'The vehicle bed must be empty and clean, with no chemicals, before loading.',
  NOT_A_BUYER: 'This customer is not registered as a buyer.',
  LOT_NOT_RELEASED: 'A pack lot on the list is not released, or is on hold.',
  OVER_DISPATCHED: 'More pouches were typed than are left at the hall.',
  UNKNOWN_PACK_LOT: 'A pack lot code on the list is not known.',
}

export interface DispatchProblem {
  packLot: string
  error: string
  pouchesLeft: number | null
}

/** errors.items[] of a refused dispatch → rows. */
export function dispatchProblems(errors: unknown): DispatchProblem[] {
  const items = errors && typeof errors === 'object' ? (errors as { items?: unknown }).items : undefined
  if (!Array.isArray(items)) return []
  return items.map((raw) => {
    const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
    return {
      packLot: String(p.pack_lot ?? ''),
      error: String(p.error ?? ''),
      pouchesLeft: typeof p.pouches_left === 'number' ? p.pouches_left : null,
    }
  })
}

/** One refused row as a sentence. */
export function problemSentence(p: DispatchProblem): string {
  const lot = p.packLot || 'A pack lot'
  switch (p.error) {
    case 'LOT_NOT_RELEASED':
      return `${lot} is not released, or is on hold — it cannot go on a trip.`
    case 'OVER_DISPATCHED':
      return p.pouchesLeft !== null
        ? `${lot}: only ${p.pouchesLeft} ${p.pouchesLeft === 1 ? 'pouch is' : 'pouches are'} left at the hall. Recount the cartons.`
        : `${lot}: more pouches than are left at the hall. Recount the cartons.`
    case 'UNKNOWN_PACK_LOT':
      return `${lot} is not one of our pack lots. Check the code or scan the carton again.`
    default:
      return `${lot}: ${humanize(p.error)}`
  }
}

// ------------------------------ correction ------------------------------

export interface CorrectionValues {
  packLot: string
  pouches: number | null
  reason: string
}
export type CorrectionErrors = Partial<Record<keyof CorrectionValues, string>>

/** `left` = pouches of that lot still at the hall, when known. */
export function validateCorrection(f: CorrectionValues, left?: number | null): CorrectionErrors {
  const e: CorrectionErrors = {}
  if (!normalizePackLotCode(f.packLot)) e.packLot = 'Scan or type the pack lot'
  if (f.pouches === null || !Number.isInteger(f.pouches) || f.pouches < 1) e.pouches = 'At least 1 pouch'
  else if (left !== undefined && left !== null && f.pouches > left)
    e.pouches = left === 0 ? 'No pouches of this lot are left at the hall' : `Only ${left} left at the hall`
  const reason = f.reason.trim()
  if (reason.length < 10) e.reason = 'Say what happened (at least 10 characters)'
  else if (reason.length > 200) e.reason = 'At most 200 characters'
  return e
}

export function toCorrectionBody(f: CorrectionValues): { packLot: string; pouches: number; reason: string } {
  return { packLot: normalizePackLotCode(f.packLot), pouches: f.pouches ?? 0, reason: f.reason.trim() }
}
